/** @format */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
	Alert,
	Avatar,
	Box,
	Button,
	Checkbox,
	Container,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import MiniAccount from "../mini-account/MiniAccount";
import { ICreateCircle } from "../../utility/types/circleType";
import { AppDispatch } from "../../redux/store/store";
import { buttonColors, colors } from "../../utility/types/constant";
import { trackCreateCircle } from "../../utility/redditPixel";
import { useNavigate } from "react-router-dom";
import ThemeProvider from "../../theme";
import { createCircle } from "../../redux/actions/circleAction";
import {
	CircleForm,
	CountDownTimer,
	NotificationModal,
} from "../allComponents/AllComponents";
import { selectIsLoading } from "../../redux/selectors/circleSelector";

const CreateCircle = () => {
	const navigate = useNavigate();

	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const isLoading = useSelector(selectIsLoading);

	const [successAlert, setSuccessAlert] = useState(false);

	const [errorAlert, setErrorAlert] = useState("");

	const [imagePreview, setImagePreview] = useState("");

	const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

	const [fileError, setFileError] = useState("");

	const [open, setOpen] = useState(false);

	// Maximum file size: 10MB
	const MAX_FILE_SIZE = 10 * 1024 * 1024;

	const initialValues: ICreateCircle = {
		circleName: "",
		circleImage: null,
		description: "",
		leader: userData?._id || "",
		membersCount: "",
		members: [],
		// contribution: [
		//   {
		//     memberId: "",
		//     contributionAmount: 0,
		//     maxContributionTime: "",
		//     contributionPeriod: "",
		//     contributionDays: [],
		//   },
		// ],
		configuration: {
			contributionAmount: 0,
			maxContributionTime: "",
			contributionPeriod: "",
			contributionFrequency: 0,
			contributionDays: [],
			payOutPeriod: "Monthly",
			payOutDay: "",
			country: "US",
			currency: "usd",
		},
		payOut: {
			userId: "",
			payOutPeriod: "",
			payOutDay: "",
		},
	};

	const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Clear previous file error
		setFileError("");

		// Check if any file is selected
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];

			// Validate file size (max 10MB)
			if (file.size > MAX_FILE_SIZE) {
				const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
				setFileError(
					`File size (${fileSizeMB}MB) exceeds the 10MB limit. Please choose a smaller image.`,
				);
				// Reset the input
				e.target.value = "";
				setSelectedFile(undefined);
				setImagePreview("");
				return;
			}

			// Validate file type
			const allowedTypes = [
				"image/jpeg",
				"image/png",
				"image/gif",
				"image/webp",
				"image/svg+xml",
				"image/bmp",
				"image/tiff",
				"image/heic",
				"image/heif",
				"image/avif",
			];

			if (!allowedTypes.includes(file.type)) {
				setFileError(
					"Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, SVG, etc.)",
				);
				e.target.value = "";
				setSelectedFile(undefined);
				setImagePreview("");
				return;
			}

			setImagePreview(URL.createObjectURL(file));
			setSelectedFile(file);
		}
	};

	useEffect(() => {
		return () => {
			if (imagePreview) {
				URL.revokeObjectURL(imagePreview);
			}
		};
	});

	const handleSubmit = async (values: ICreateCircle) => {
		try {
			const formData = new FormData();

			formData.append("circleName", values.circleName);
			formData.append("description", values.description);
			formData.append("leader", userData?._id ?? "");
			formData.append("membersCount", values.membersCount);

			if (values.members) {
				values.members.forEach((member) => formData.append("members", member));
			}

			// // Handle contribution array
			// if (values.contribution) {
			//   values.contribution.forEach((contribution, index) => {
			//     formData.append(
			//       `contribution[${index}][contributionAmount]`,
			//       String(contribution.contributionAmount)
			//     );
			//     formData.append(
			//       `contribution[${index}][maxContributionTime]`,
			//       contribution.maxContributionTime ?? ""
			//     );
			//     formData.append(
			//       `contribution[${index}][contributionPeriod]`,
			//       contribution.contributionPeriod
			//     );
			//     // Stringify contributionDays array
			//     formData.append(
			//       `contribution[${index}][contributionDays]`,
			//       JSON.stringify(contribution.contributionDays)
			//     );
			//   });
			// }

			// Handle configuration object
			if (values.configuration) {
				formData.append(
					"configuration[contributionAmount]",
					String(values.configuration.contributionAmount),
				);
				formData.append(
					"configuration[maxContributionTime]",
					values.configuration.maxContributionTime ?? "",
				);
				formData.append(
					"configuration[contributionPeriod]",
					values.configuration.contributionPeriod,
				);
				formData.append(
					"configuration[contributionFrequency]",
					String(values.configuration.contributionFrequency),
				);
				formData.append(
					"configuration[contributionDays]",
					JSON.stringify(values.configuration.contributionDays),
				);
				formData.append("configuration[payOutPeriod]", "Monthly");

				if (values.configuration.payOutDay) {
					// If it's an array (from a multi-select), you might send just the first element or the whole array depending on your schema.
					formData.append(
						"configuration[payOutDay]",
						values.configuration.payOutDay ?? "",
					);
				}

				formData.append(
					"configuration[country]",
					values.configuration.country ?? "US",
				);
				formData.append(
					"configuration[currency]",
					values.configuration.currency ?? "usd",
				);
			}

			// // Handle payOut array
			// if (values.payOut) {
			//   formData.append("payOut[payOutPeriod]", values.payOut.payOutPeriod);

			//   const payoutDay = Array.isArray(values.payOut.payOutDay)
			//     ? values.payOut.payOutDay[0]
			//     : values.payOut.payOutDay;

			//   formData.append("payOut[payOutDay]", payoutDay);
			// }

			// Handle file upload
			if (selectedFile) {
				formData.append("circleImage", selectedFile);
			}

			// Dispatch the action
			const response = await dispatch(createCircle(formData)).unwrap();
			console.log("response after creating circle ===>", response);

			if (response?._id) {
				trackCreateCircle(response._id);
				setSuccessAlert(true);
				setOpen(true);
				setTimeout(() => {
					navigate("/my-circles");
				}, 10000);
			} else {
				setSuccessAlert(false);
				setOpen(false);
				setErrorAlert("Unknown error occurred.");
			}
		} catch (error: any) {
			setSuccessAlert(false);
			setOpen(false);
			setErrorAlert(error.message || "Unknown error occurred.");
		}
	};

	const handleCloseSuccessAlert = () => {
		setSuccessAlert(false);
	};

	const handleCloseErrorAlert = () => {
		setErrorAlert(""); // Set the error message to an empty string to hide the alert
	};

	const handleCloseFileError = () => {
		setFileError("");
	};

	useEffect(() => {
		let timeOutId: any;
		return () => {
			if (timeOutId) {
				clearTimeout(timeOutId);
			}
		};
	});

	const handleBtnClick = () => {
		navigate(`/user/${userData?._id}`);
	};

	const handleClose = () => {
		setOpen(false);
	};

	return (
		<ThemeProvider>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 8,
					justifyContent: "center",
					display: "flex",
				}}>
				<Container
					maxWidth="lg"
					sx={{
						mt: 7,
					}}>
					{successAlert && (
						<Stack sx={{ width: "100%" }} spacing={2}>
							<Alert severity="success" onClose={handleCloseSuccessAlert}>
								Success -{" "}
								<strong>Congratulations!! you just formed your circle</strong>
							</Alert>
						</Stack>
					)}

					{errorAlert && (
						<Stack sx={{ width: "100%" }} spacing={2}>
							<Alert severity="error" onClose={handleCloseErrorAlert}>
								Error -{" "}
								<strong>
									{errorAlert ?
										errorAlert
										: "Error occurred during circle formation."}
								</strong>
							</Alert>
						</Stack>
					)}

					{fileError && (
						<Stack sx={{ width: "100%" }} spacing={2}>
							<Alert severity="warning" onClose={handleCloseFileError}>
								<strong>{fileError}</strong>
							</Alert>
						</Stack>
					)}

					<Stack spacing={8} sx={{ width: "100%" }}>
						<div>
							<Button
								onClick={handleBtnClick}
								variant="contained"
								size="large"
								sx={{
									color: colors.white,
									bgcolor: buttonColors.primary,
									"&:hover": {
										bgcolor: buttonColors.primary,
									},
								}}>
								Back to Dashboard
							</Button>
						</div>

						<div>
							<Grid container spacing={5}>
								<Grid item xs={12} md={6} lg={4} sx={{ padding: 2 }}>
									<MiniAccount
										name={userData?.firstName ?? ""}
										image={userData?.profileImage ?? ""}
										email={userData?.email ?? ""}
									/>
								</Grid>

								<Grid item xs={12} md={6} lg={8} sx={{ padding: 2 }}>
									<CircleForm
										userData={userData}
										initialValues={initialValues}
										handleSelectFile={handleSelectFile}
										imagePreview={imagePreview}
										handleSubmit={handleSubmit}
										subTitle="Set Config Settings For Your Circle"
										title="Create Your Circle"
										isLoading={isLoading}
									/>
								</Grid>
							</Grid>
						</div>
					</Stack>

					{open && (
						<NotificationModal open={open} handleClose={handleClose}>
							<Grid container spacing={2} direction="row" alignItems="center">
								<Grid item>
									<Avatar
										src="/assets/icons/congrats-icon.gif"
										sx={{ width: 30, height: 30 }}
										alt="Congrats Icon"
									/>
								</Grid>
								<Grid item xs>
									<Typography
										variant="body2"
										sx={{
											fontSize: 15,
											color: "text.secondary",
											display: "flex",
											alignItems: "center",
											gap: "8px",
											flexWrap: "wrap",
										}}>
										Congratulations! You successfully created your Circle. Now
										redirecting you to view your Circle in
										<CountDownTimer
											seconds={10}
											onFinish={handleClose}
											sx={{ margin: 0 }}
										/>
									</Typography>
								</Grid>
							</Grid>
						</NotificationModal>
					)}
				</Container>
			</Box>
		</ThemeProvider>
	);
};

export default CreateCircle;

interface PayOutDayMultiSelectProps {
	field: any;
	form: any;
	options: any;
	label: any;
	error: any;
}

export const PayOutDayMultiSelect = (props: PayOutDayMultiSelectProps) => {
	const { field, form, options, label } = props;

	const handleChange = (event: any) => {
		const { value } = event.target;
		form.setFieldValue(field.name, value);

		// if (value.length <= 1) {
		//   form.setFieldValue(field.name, value);
		// }
	};

	return (
		<FormControl
			fullWidth
			sx={{
				width: "100%",
				fontSize: "1.2rem",
				"& .MuiOutlinedInput-root": {
					"& fieldset": {
						borderColor: "black",
					},
					"&:hover fieldset": {
						borderColor: "black",
					},
					"&.Mui-focused fieldset": {
						borderColor: "black",
					},
				},
			}}>
			<InputLabel id="PayOutDays-label">{label}</InputLabel>
			<Select
				labelId="payOutDays-label"
				id="payOutDays-label-helper"
				value={field.value}
				// value={
				//   Array.isArray(field.value)
				//     ? field.value
				//     : field.value
				//     ? [field.value]
				//     : []
				// }
				label="PayOut Day"
				name={field.name}
				onChange={handleChange}
			// renderValue={(selected) => selected.join(", ")}
			// multiple
			>
				{/* {options.map((option: any) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={field?.value?.indexOf(option.value) > -1} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))} */}
				{options.map((option: any) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</Select>
			<FormHelperText>{"PayOut Day Required"}</FormHelperText>
		</FormControl>
	);
};

PayOutDayMultiSelect.propTypes = {
	field: PropTypes.any,
	form: PropTypes.any,
	options: PropTypes.any,
	label: PropTypes.any,
	error: PropTypes.any,
};

interface MultiSelectWithLimitProps {
	field: any;
	form: any;
	options: any;
	label: any;
	error: any;
}

export const MultiSelectWithLimit = (props: MultiSelectWithLimitProps) => {
	const { field, form, options, label } = props;
	const handleChange = (event: any) => {
		const { value } = event.target;
		form.setFieldValue(field.name, value);
	};

	return (
		<FormControl
			fullWidth
			sx={{
				width: "100%",
				fontSize: "1.2rem",
				"& .MuiOutlinedInput-root": {
					"& fieldset": {
						borderColor: "black",
					},
					"&:hover fieldset": {
						borderColor: "black",
					},
					"&.Mui-focused fieldset": {
						borderColor: "black",
					},
				},
			}}>
			<InputLabel id="ContributionDays-label">{label}</InputLabel>
			<Select
				labelId="contributionDays-label"
				id="contributionDays-label-helper"
				// value={field.value}
				value={Array.isArray(field.value) ? field.value : []}
				label="Contribution Days"
				name="contributionDays"
				onChange={handleChange}
				renderValue={(selected) => selected.join(", ")}
				multiple>
				{options?.map((option: any) => (
					<MenuItem key={option.value} value={option.value}>
						<Checkbox checked={field?.value?.indexOf(option.value) > -1} />
						<ListItemText primary={option.label} />
					</MenuItem>
				))}
			</Select>
			<FormHelperText>{"Contribution Day Required"}</FormHelperText>
		</FormControl>
	);
};

MultiSelectWithLimit.propTypes = {
	field: PropTypes.any,
	form: PropTypes.any,
	options: PropTypes.any,
	label: PropTypes.any,
	error: PropTypes.any,
};
